import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { useApi } from '@src/app/context/Api';
import { User } from '@src/entities/User/ui/components/User';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';

import fields from './includes/fields.json';

const { eids } = fields;

/**
 * Button: AddEid
 *
 * Renders a button that opens a modal for adding EIDs to the action path's associated EID list.
 *
 * @param actionPath
 * @param refetch
 * @returns {Element}
 * @constructor
 */
export function AddEid({ actionPath = {}, refetch = () => {} }) {
  const [showModal, setShowModal] = useState(false);

  const { toast } = useToast();

  const { clearApiCache, useApiMutation } = useApi('RiskRecord');
  const [RiskRecordActionPathAssociation] = useApiMutation('RiskRecordActionPathAssociation', {
    excludedHttpCodes: [400],
  });

  const defaultValues = {
    eids: [],
  };

  const form = useForm({ defaultValues });

  /**
   * handleClose
   *
   * Handles the closing of the modal.
   *
   * @returns {Promise<void>}
   */
  const handleClose = async () => {
    form?.reset(defaultValues, {
      keepDirty: false,
      keepDirtyValues: false,
      keepErrors: false,
      keepIsValid: false,
      keepSubmitCount: true,
      keepTouched: false,
      keepValues: false,
    });

    return setShowModal(false);
  };

  /**
   * handleAssociation
   *
   * Sends an api request to associate the EID's to the action path.
   *
   * @param theEids
   * @returns {Promise<void>}
   */
  const handleAssociation = async (theEids = []) => {
    const toastId = 'eid-association';
    toast.show({
      ariaLoadingLabel: "Associating EID's to Action Path",
      autoClose: false,
      id: `${toastId}-info`,
      isLoading: true,
      message: "Action Path is preparing to associate the EID's.",
      title: "Associating EID's to Action Path...",
      variant: 'info',
    });

    const payload = {
      actionPaths: [actionPath?.id],
      eids: theEids,
    };

    await RiskRecordActionPathAssociation(payload, {
      onError: () => {
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-error`,
          message: "Unable to associate EID's.",
          title: "Associating EID's Failed",
          variant: 'error',
        });
      },
      onSuccess: async (response) => {
        await clearApiCache([{ apiKey: 'RiskRecord', queryKeys: ['ListRiskRecords'] }]);
        toast.hide(`${toastId}-info`);

        if (response?.errorList?.length !== theEids?.length) {
          toast.show({
            id: `${toastId}-success`,
            message: "EID's has been Associated.",
            title: "Associated EID's to Action Path",
            variant: 'success',
          });
        }

        if (!isEmpty(response?.errorList)) {
          toast.show({
            autoClose: false,
            id: `${toastId}-partial-error`,
            message: (
              <Layout.Stack alignItems="left">
                {response?.errorList?.map((error) => {
                  return <div key={error}>{error}</div>;
                })}
              </Layout.Stack>
            ),
            title: "Failed to Associate Some EID's",
            variant: 'error',
          });
        }

        setTimeout(() => {
          refetch();
        }, 750);
      },
    });
  };

  /**
   * handleSubmit
   *
   * Handles the form submission.
   *
   * @param submittedValues
   * @returns {Promise<void>}
   */
  const handleSubmit = async (submittedValues) => {
    await handleClose();

    // Extracts EIDs from a string, splitting by comma, pipe, line break, or any whitespace.
    // Trims each EID and removes any empty entries.
    const theEids = submittedValues?.eids
      ?.split(/[,|\n\s]+/)
      .map((item) => {
        return item.trim();
      })
      .filter((item) => {
        return item;
      });

    await handleAssociation(theEids);

    form?.reset(defaultValues, {
      keepDirty: false,
      keepDirtyValues: false,
      keepErrors: false,
      keepIsValid: false,
      keepSubmitCount: true,
      keepTouched: false,
      keepValues: false,
    });
  };

  return (
    <ErrorHandler location="src/features/ActionPaths/AddEid/AddEid.jsx">
      <User.Capability attributes={['attach']} resource="ActionPaths">
        <Button
          before={<IconSymbol icon="add" />}
          css={{
            marginLeft: themeConfiguration?.theme?.space?.md,
          }}
          onClick={() => {
            return setShowModal(true);
          }}
          size="$sm"
          variant="outline"
        >
          Add EID
        </Button>
      </User.Capability>

      <User.Capability attributes={['attach']} method="hide" resource="ActionPaths">
        <Modal isOpen={showModal} onClose={handleClose} title="Add EID's">
          <FormProvider autoComplete="off" highlighted onSubmit={handleSubmit} state={form}>
            <Modal.Section>
              <Grid>
                <Grid.Col
                  span={{
                    xs: '100%',
                  }}
                >
                  <TextInputArea {...eids} disableAutoSize rows={5} />
                </Grid.Col>
              </Grid>
            </Modal.Section>
            <Modal.Section>
              <Divider height={1} />
              <Layout.Group alignLayout="right">
                <Button onClick={handleClose} variant="outline">
                  Cancel
                </Button>
                <Button isDisabled={isEmpty(form?.getValues('eids'))} type="submit" variant="solid">
                  Add
                </Button>
              </Layout.Group>
            </Modal.Section>
          </FormProvider>
        </Modal>
      </User.Capability>
    </ErrorHandler>
  );
}
